import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ChevronRight, Menu, Star, type LucideIcon } from 'lucide-react';
import { Input } from '@auw/workspace-ui';
import {
  SidebarPane,
  SidebarHeader,
  SidebarHeaderLeft,
  SidebarTitle,
  SidebarToggleBtn,
  SidebarTreeWrap,
  TreeNodeLabel,
  SectionDivider,
  SidebarSection,
  SidebarSectionHeader,
  SectionLabel,
  SectionChevron,
  TreeNodeRow,
  EmptyHint,
} from '@auw/workspace-ui';

export type ToolCategoryId =
  | 'calculation'
  | 'time'
  | 'productivity'
  | 'text'
  | 'developer'
  | 'file-image'
  | 'random';

export type ToolId =
  | 'calculator'
  | 'unit-converter'
  | 'percentage'
  | 'date-diff'
  | 'd-day'
  | 'timer-stopwatch'
  | 'memo'
  | 'checklist'
  | 'text-counter'
  | 'text-transform'
  | 'json-formatter'
  | 'uuid-generator'
  | 'timestamp-converter'
  | 'image-info'
  | 'image-resizer'
  | 'random-picker'
  | 'coin-dice';

export interface ToolDefinition {
  id: ToolId;
  label: string;
  description: string;
  shortLabel?: string;
}

export interface ToolCategory {
  id: ToolCategoryId;
  label: string;
  description: string;
  icon: LucideIcon;
  tools: ToolDefinition[];
}

interface Props {
  categories: ToolCategory[];
  selectedToolId: ToolId;
  openCategoryIds: Set<ToolCategoryId>;
  onToggleCategory: (categoryId: ToolCategoryId) => void;
  onSelectTool: (toolId: ToolId) => void;
  pageSidebarOpen: boolean;
  onTogglePageSidebar: () => void;
  desktopHidden?: boolean;
  favoriteToolIds: string[];
  onToggleFavorite: (toolId: ToolId) => void;
}

interface SearchMatch {
  tool: ToolDefinition;
  categoryLabel: string;
}

function buildSearchMatches(
  categories: ToolCategory[],
  query: string,
): SearchMatch[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const results: SearchMatch[] = [];
  for (const cat of categories) {
    for (const tool of cat.tools) {
      if (tool.label.toLowerCase().includes(q) || tool.description.toLowerCase().includes(q)) {
        results.push({ tool, categoryLabel: cat.label });
      }
    }
  }
  return results;
}

export const ToolsSidebar: React.FC<Props> = ({
  categories,
  selectedToolId,
  openCategoryIds,
  onToggleCategory,
  onSelectTool,
  pageSidebarOpen,
  onTogglePageSidebar,
  desktopHidden,
  favoriteToolIds,
  onToggleFavorite,
}) => {
  const { t } = useTranslation('tools');
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const searchMatches = useMemo(
    () => buildSearchMatches(categories, searchQuery),
    [categories, searchQuery],
  );

  const showDropdown = isSearchFocused && searchQuery.trim().length > 0;

  useEffect(() => {
    setHighlightIndex(-1);
  }, [searchQuery]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showDropdown) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev < searchMatches.length - 1 ? prev + 1 : 0,
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : searchMatches.length - 1,
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const idx = highlightIndex >= 0 ? highlightIndex : 0;
        const match = searchMatches[idx];
        if (match) {
          onSelectTool(match.tool.id);
          setSearchQuery('');
          setIsSearchFocused(false);
        }
      } else if (e.key === 'Escape') {
        setSearchQuery('');
        setIsSearchFocused(false);
      }
    },
    [showDropdown, searchMatches, highlightIndex, onSelectTool],
  );

  const handleSelectSearchResult = useCallback(
    (toolId: ToolId) => {
      onSelectTool(toolId);
      setSearchQuery('');
      setIsSearchFocused(false);
    },
    [onSelectTool],
  );

  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  useEffect(() => {
    if (highlightIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[highlightIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const allTools = useMemo(
    () => categories.flatMap((cat) => cat.tools),
    [categories],
  );

  const favoriteTools = useMemo(
    () => favoriteToolIds
      .map((id) => allTools.find((t) => t.id === id))
      .filter((t): t is ToolDefinition => t !== undefined),
    [favoriteToolIds, allTools],
  );

  const favoriteSet = useMemo(
    () => new Set(favoriteToolIds),
    [favoriteToolIds],
  );

  return (
    <SidebarPane $hidden={desktopHidden}>
      <SidebarHeader>
        <SidebarHeaderLeft>
          <SidebarTitle>{t('sidebar.title')}</SidebarTitle>
        </SidebarHeaderLeft>
        <SidebarToggleBtn
          type="button"
          onClick={onTogglePageSidebar}
          title={pageSidebarOpen ? t('sidebar.collapse') : t('sidebar.expand')}
        >
          <Menu size={16} />
        </SidebarToggleBtn>
      </SidebarHeader>

      <SearchArea ref={searchWrapRef}>
        <Input
          variant="search"
          inputSize="sm"
          placeholder={t('sidebar.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          onFocus={() => setIsSearchFocused(true)}
        />
        {showDropdown && (
          <SearchDropdown ref={listRef}>
            {searchMatches.length === 0 ? (
              <SearchEmpty>{t('sidebar.noResults')}</SearchEmpty>
            ) : (
              searchMatches.map((match, idx) => (
                <SearchResultItem
                  key={match.tool.id}
                  $active={idx === highlightIndex}
                  onMouseDown={() => handleSelectSearchResult(match.tool.id)}
                  onMouseEnter={() => setHighlightIndex(idx)}
                >
                  <SearchResultLabel>{match.tool.label}</SearchResultLabel>
                  <SearchResultCategory>{match.categoryLabel}</SearchResultCategory>
                </SearchResultItem>
              ))
            )}
          </SearchDropdown>
        )}
      </SearchArea>

      <SidebarTreeWrap>
        {categories.length === 0 ? (
          <EmptyHint>{t('sidebar.loading')}</EmptyHint>
        ) : (
          <>
            {favoriteTools.length > 0 && (
              <>
                <SidebarSection>
                  <SidebarSectionHeader as="div">
                    <FavSectionIcon>
                      <Star size={13} fill="#f5b301" stroke="#f5b301" />
                    </FavSectionIcon>
                    <SectionLabel>{t('sidebar.favorites')}</SectionLabel>
                  </SidebarSectionHeader>
                  {favoriteTools.map((tool) => (
                    <ToolRow
                      key={`fav-${tool.id}`}
                      type="button"
                      $depth={2}
                      $active={selectedToolId === tool.id}
                      onClick={() => onSelectTool(tool.id)}
                    >
                      <TreeNodeLabel>{tool.label}</TreeNodeLabel>
                      <FavBtn
                        $favorited
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onToggleFavorite(tool.id); }}
                        title={t('sidebar.removeFavorite')}
                      >
                        <Star size={13} />
                      </FavBtn>
                    </ToolRow>
                  ))}
                </SidebarSection>
                <SectionDivider />
              </>
            )}

            {categories.map((category, index) => {
              const isOpen = openCategoryIds.has(category.id);
              return (
                <React.Fragment key={category.id}>
                  {index > 0 && <SectionDivider />}
                  <SidebarSection>
                    <SidebarSectionHeader
                      type="button"
                      onClick={() => onToggleCategory(category.id)}
                    >
                      <SectionChevron $open={isOpen}>
                        <ChevronRight size={14} />
                      </SectionChevron>
                      <SectionLabel>{category.label}</SectionLabel>
                    </SidebarSectionHeader>

                    {isOpen && category.tools.map((tool) => (
                      <ToolRow
                        key={tool.id}
                        type="button"
                        $depth={2}
                        $active={selectedToolId === tool.id}
                        onClick={() => onSelectTool(tool.id)}
                      >
                        <TreeNodeLabel>{tool.label}</TreeNodeLabel>
                        <FavBtn
                          $favorited={favoriteSet.has(tool.id)}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onToggleFavorite(tool.id); }}
                          title={favoriteSet.has(tool.id) ? t('sidebar.removeFavorite') : t('sidebar.addFavorite')}
                        >
                          <Star size={13} />
                        </FavBtn>
                      </ToolRow>
                    ))}
                  </SidebarSection>
                </React.Fragment>
              );
            })}
          </>
        )}
      </SidebarTreeWrap>
    </SidebarPane>
  );
};

/* ------------------------------------------------------------------ */
/* Styled                                                              */
/* ------------------------------------------------------------------ */

const SearchArea = styled.div`
  position: relative;
  padding: 6px 10px 4px;
`;

const SearchDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 10px;
  right: 10px;
  z-index: 20;
  max-height: 240px;
  overflow-y: auto;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 4px 16px rgba(0,0,0,0.5)'
      : '0 4px 16px rgba(0,0,0,0.10)'};
  padding: 4px;
`;

const SearchResultItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.1s;

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.05)'};
    `}

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.05)'};
  }
`;

const SearchResultLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SearchResultCategory = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  flex-shrink: 0;
`;

const SearchEmpty = styled.div`
  padding: 12px 10px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
`;

const FavBtn = styled.button<{ $favorited: boolean }>`
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s, transform 0.15s;

  color: ${({ $favorited }) => ($favorited ? '#f5b301' : 'transparent')};
  visibility: ${({ $favorited }) => ($favorited ? 'visible' : 'hidden')};

  ${({ $favorited, theme }) =>
    !$favorited &&
    css`
      &:hover {
        color: ${theme.colors.textMuted};
      }
    `}

  ${({ $favorited }) =>
    $favorited &&
    css`
      svg {
        fill: #f5b301;
      }
      &:hover {
        transform: scale(1.15);
      }
    `}
`;

const ToolRow = styled(TreeNodeRow)`
  &:hover ${FavBtn} {
    visibility: visible;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const FavSectionIcon = styled.span`
  display: flex;
  align-items: center;
  margin-right: 2px;
`;
